import {
    ModifyOrderFormValues,
    BondQuantityType,
    OrderPreview,
    OrderRef,
    PriceType,
} from '@orbis-systems/orbis-api-middleware';
import { SuccessClientResponse } from '@orbis-systems/orbis-api-clients';
import { queryOptions } from '@tanstack/react-query';

import {
    InventoryEquityPreviewPayload,
    AccountEquityPreviewPayload,
    InventoryEquityPlacePayload,
    SingleLegOptionPreviewPayload,
    SingleLegOptionPlacePayload,
    MultilegOptionPreviewPayload,
    MultilegOptionPlacePayload,
    ModelEquityPreviewPayload,
    AccountEquityPlacePayload,
    AlgoEquityPreviewPayload,
    ModelEquityPlacePayload,
    AlgoEquityPlacePayload,
    BondOrderPreviewPayload,
    BondOrderPlacePayload,
    MutualFundOrderPreviewPayload,
    MutualFundOrderPlacePayload,
} from './types';
import { orbis, orbisMiddleware } from '../api';

export interface TradingModifyOrderParams {
    values: ModifyOrderFormValues;
    accountId: number;
    accountNumber: string;
    marketCode: string;
}

export interface TradingCancelOrderParams {
    orderRef: string;
    accountId: number;
    marketCode: string;
}

export interface TradingGetOrderDetailsParams {
    accountId: number;
    orderRef: string;
    market: string;
    loadQuotes: boolean;
    bondPriceType: PriceType;
    bondQuantityType: BondQuantityType;
}

export const tradingQueries = {
    all: () => queryOptions({ queryKey: ['trading'] }),
    orderStatus: (params: TradingGetOrderDetailsParams) =>
        queryOptions({
            queryKey: [...tradingQueries.all().queryKey, 'orderStatus', params],
            queryFn: () =>
                orbis.orders.status(params.orderRef, {
                    accountId: params.accountId,
                    loadQuotes: params.loadQuotes,
                    bondPriceType: params.bondPriceType,
                    bondQuantityType: params.bondQuantityType,
                    market: params.market,
                }),
        }),
} as const;

export const tradingMutations = {
    previewAccountEquityOrder: (params: AccountEquityPreviewPayload): Promise<SuccessClientResponse<OrderPreview>> =>
        orbis.orders.v2.equities.preview(params),
    previewModelEquityOrder: (params: ModelEquityPreviewPayload): Promise<SuccessClientResponse<OrderPreview>> =>
        orbis.orders.v2.equities.advisoryPreview(params),
    previewAlgoEquityOrder: (params: AlgoEquityPreviewPayload): Promise<SuccessClientResponse<OrderPreview>> =>
        orbis.orders.v2.equities.algoPreview(params),
    previewInventoryEquityOrder: (
        params: InventoryEquityPreviewPayload,
    ): Promise<SuccessClientResponse<OrderPreview>> => orbis.orders.v2.equities.advisoryPreview(params),
    placeInventoryEquityOrder: (params: InventoryEquityPlacePayload): Promise<SuccessClientResponse<OrderRef>> =>
        orbis.orders.v2.equities.advisoryPlacement(params),
    placeAccountEquityOrder: (params: AccountEquityPlacePayload): Promise<SuccessClientResponse<OrderRef>> =>
        orbis.orders.v2.equities.placement(params),
    placeModelEquityOrder: (params: ModelEquityPlacePayload): Promise<SuccessClientResponse<OrderRef>> =>
        orbis.orders.v2.equities.advisoryPlacement(params),
    placeAlgoEquityOrder: (params: AlgoEquityPlacePayload): Promise<SuccessClientResponse<OrderRef>> =>
        orbis.orders.v2.equities.algoPlacement(params),
    previewSingLegOptionOrder: (params: SingleLegOptionPreviewPayload): Promise<SuccessClientResponse<OrderPreview>> =>
        orbis.orders.v2.options.preview(params),
    placeSingleLegOptionOrder: (params: SingleLegOptionPlacePayload): Promise<SuccessClientResponse<OrderRef>> =>
        orbis.orders.v2.options.placement(params),
    previewMultilegOptionOrder: (params: MultilegOptionPreviewPayload): Promise<SuccessClientResponse<OrderPreview>> =>
        orbis.orders.v2.options.previewMultileg(params),
    placeMultilegOptionOrder: (params: MultilegOptionPlacePayload): Promise<SuccessClientResponse<OrderRef>> =>
        orbis.orders.v2.options.placementMultileg(params),
    previewBondOrder: (params: BondOrderPreviewPayload): Promise<SuccessClientResponse<OrderPreview>> =>
        orbis.orders.v2.fixedIncome.bondOrderPreview(params),
    placeBondOrder: (params: BondOrderPlacePayload): Promise<SuccessClientResponse<OrderRef>> =>
        orbis.orders.v2.fixedIncome.bondPlacement(params),
    previewMutualFundOrder: (params: MutualFundOrderPreviewPayload): Promise<SuccessClientResponse<OrderPreview>> =>
        orbis.orders.v2.mutualFunds.mutualFundPreview(params),
    placeMutualFundOrder: (params: MutualFundOrderPlacePayload): Promise<SuccessClientResponse<OrderRef>> =>
        orbis.orders.v2.mutualFunds.mutualFundPlacement(params),
    modifyOrder: (params: TradingModifyOrderParams): Promise<string> => orbisMiddleware.trading.modifyOrder(params),
    cancelOrder: (params: TradingCancelOrderParams): Promise<string> =>
        orbisMiddleware.trading.cancelOrder(params.orderRef, params.accountId, params.marketCode),
} as const;
